import React, { useState } from 'react';
import './LessonSummary.css';

const LessonSummary = ({ lesson, validationResults, submissions, onRedo, onClose, errorMessage }) => {
  const [showRedoForm, setShowRedoForm] = useState(false);
  const [difficulties, setDifficulties] = useState('');
  const [redoLoading, setRedoLoading] = useState(false);

  // Calculate statistics
  const totalTasks = lesson.tasks.length;
  const completedTasks = Object.keys(validationResults || {}).length;
  const correctTasks = Object.values(validationResults || {}).filter(v => v && v.isValid).length;
  const needsImprovement = Object.values(validationResults || {}).filter(v => v && !v.isValid).length;

  // Collect what they did well
  const goodThings = [];
  const improvements = [];

  if (lesson.tasks && validationResults) {
    lesson.tasks.forEach((task, index) => {
      const validation = validationResults[task.taskNumber];
      const submission = submissions[task.taskNumber];
      const attempts = submission?.attempts || 0;
      
      if (validation) {
        if (validation.isValid) {
          goodThings.push({
            taskNumber: task.taskNumber,
            description: task.description,
            feedback: validation.feedback,
            attempts: attempts
          });
        } else {
          improvements.push({
            taskNumber: task.taskNumber,
            description: task.description,
            feedback: validation.feedback,
            suggestions: validation.suggestions || [],
            attempts: attempts
          });
        }
      }
    });
  }

  const handleRedo = async () => {
    if (!difficulties.trim()) {
      alert('Beskriv hva du synes er vanskelig');
      return;
    }

    setRedoLoading(true);
    try {
      await onRedo(difficulties);
    } catch (error) {
      console.error('Failed to redo quiz:', error);
    } finally {
      setRedoLoading(false);
    }
  };

  return (
    <div className="lesson-summary-container">
      <div className="lesson-summary-card">
        <h2>Leksjon fullført!</h2>
        <p className="summary-subtitle">Slik gikk det:</p>

        <div className="summary-stats">
          <div className="stat-item">
            <div className="stat-number">{completedTasks}/{totalTasks}</div>
            <div className="stat-label">Oppgaver fullført</div>
          </div>
          <div className="stat-item stat-correct">
            <div className="stat-number">{correctTasks}</div>
            <div className="stat-label">Riktig</div>
          </div>
          <div className="stat-item stat-improvement">
            <div className="stat-number">{needsImprovement}</div>
            <div className="stat-label">Trenger forbedring</div>
          </div>
        </div>

        {goodThings.length > 0 && (
          <div className="summary-section good-things">
            <h3>Dette gjorde du bra</h3>
            <div className="items-list">
              {goodThings.map((item, index) => (
                <div key={index} className="summary-item">
                  <div className="item-header">
                    <span className="item-number">Oppgave {item.taskNumber}</span>
                    <span className="item-title">{item.description}</span>
                  </div>
                  <p className="item-feedback">{item.feedback}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {improvements.length > 0 && (
          <div className="summary-section improvements">
            <h3>Områder for forbedring</h3>
            <div className="items-list">
              {improvements.map((item, index) => (
                <div key={index} className="summary-item">
                  <div className="item-header">
                    <span className="item-number">Oppgave {item.taskNumber}</span>
                    <span className="item-title">{item.description}</span>
                    {item.attempts > 0 && (
                      <span className="item-attempts">Forsøk: {item.attempts}</span>
                    )}
                  </div>
                  <p className="item-feedback">{item.feedback}</p>
                  {item.suggestions && item.suggestions.length > 0 && (
                    <ul className="item-suggestions">
                      {item.suggestions.map((suggestion, idx) => (
                        <li key={idx}>{suggestion}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="summary-error">
            {errorMessage}
          </div>
        )}

        <div className="summary-actions">
          {!showRedoForm ? (
            <>
              <button 
                className="redo-btn"
                onClick={() => setShowRedoForm(true)}
              >
                Gjør på nytt med lignende oppgaver
              </button>
              <button 
                className="close-btn"
                onClick={onClose}
              >
                Lukk
              </button>
            </>
          ) : (
            <div className="redo-form">
              <h4>Hva vil du øve mer på?</h4>
              <p className="form-hint">Skriv hvilke temaer eller begreper du syntes var vanskelig. Vi lager en ny øvingsleksjon med lignende oppgaver for å hjelpe deg å bli bedre.</p>
              <textarea
                value={difficulties}
                onChange={(e) => setDifficulties(e.target.value)}
                placeholder="f.eks. Jeg syntes skjemavalidering, event handlers og CSS flexbox var vanskelig..."
                rows="4"
                className="difficulties-input"
              />
              <div className="redo-form-actions">
                <button 
                  className="generate-btn"
                  onClick={handleRedo}
                  disabled={redoLoading || !difficulties.trim()}
                >
                  {redoLoading ? 'Oppretter ny leksjon...' : 'Opprett øvingsleksjon'}
                </button>
                <button 
                  className="cancel-redo-btn"
                  onClick={() => {
                    setShowRedoForm(false);
                    setDifficulties('');
                  }}
                  disabled={redoLoading}
                >
                  Avbryt
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonSummary;
